import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  // Apple as AppleIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { useAuthStore } from '../context/stores';
import { apiForgotPassword } from '../services/api';
import useToast from '../hooks/useToast';

import login1 from '../assets/login_1.jpg';
import login2 from '../assets/login_2.jpg';
import login3 from '../assets/login_3.jpg';


const BG_IMAGES = [login1, login2, login3];

const AUTH_ERROR_MESSAGES = [
  { match: 'auth/invalid-credential', message: 'Invalid email or password. Please try again.' },
  { match: 'auth/user-not-found', message: 'No account found with that email.' },
  { match: 'auth/wrong-password', message: 'Invalid email or password. Please try again.' },
  { match: 'auth/popup-closed-by-user', message: 'Sign in was cancelled.' },
  { match: 'auth/account-exists-with-different-credential', message: 'An account already exists with a different sign-in method.' },
  { match: 'auth/network-request-failed', message: 'Network error. Please check your connection and try again.' },
];

function getFriendlyAuthError(error) {
  if (!error) return 'Sign in failed. Please try again.';
  const raw = String(error);
  const found = AUTH_ERROR_MESSAGES.find(({ match }) => raw.includes(match));
  return found?.message || raw.replace(/^Firebase:\s*/i, '').replace(/\.?\s*\(auth\/[^)]+\)\.?$/, '.');
}

export default function LoginPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const {
    login,
    signup,
    loginWithProvider,
    loading,
    error,
    clearError,
  } = useAuthStore();
  const toast = useToast();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [bgIndex, setBgIndex] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);

  // Background image carousel timer
  useEffect(() => {
    const timer = setInterval(() => setBgIndex((i) => (i + 1) % BG_IMAGES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!error) return;
    toast.error(getFriendlyAuthError(error), { replace: true });
    clearError();
  }, [error, toast, clearError]);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Enter a valid email';

    if (mode !== 'forgot') {
      if (!password) e.password = 'Password is required';
      else if (mode === 'signup' && password.length < 8) e.password = 'At least 8 characters';
    }

    if (mode === 'signup' && !name.trim()) e.name = 'Your name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    if (mode === 'forgot') {
      try {
        setResetLoading(true);
        await apiForgotPassword(email);
        toast.success('Reset link sent. Check your inbox.');
        setTimeout(() => {
          setMode('login');
        }, 2500);
      } catch (err) {
        toast.error(err?.message || 'Could not send reset link. Please try again.');
      } finally {
        setResetLoading(false);
      }
      return;
    }

    // We just call the auth functions.
    // If they succeed, Zustand's token updates, and App.jsx handles the transition automatically!
    if (mode === 'login') {
      await login(email, password);
    } else {
      await signup(name, email, password);
    }
  };

  const handleSocialLogin = async (provider) => {
    clearError();
    await loginWithProvider(provider);
    // Again, App.jsx takes over once the Zustand store updates with a token.
  };

  const switchMode = (m) => {
    setMode(m);
    setErrors({});
    clearError();
  };

  const titles = {
    login: { heading: 'Welcome back', sub: '' },
    signup: { heading: 'Create your account', sub: '' },
    forgot: { heading: 'Reset password', sub: "We'll send you a reset link" },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Left image panel (desktop) ── */}
      {isDesktop && (
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {BG_IMAGES.map((img, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: bgIndex === i ? 1 : 0,
                transition: 'opacity 1.5s ease-in-out',
              }}
            />
          ))}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 100%)',
            }}
          />
          <Box sx={{ position: 'absolute', bottom: 48, left: 48, right: 48, zIndex: 2 }}>
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 300, mb: 1.5, textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
              Personalized keepsakes,<br />beautifully made for your child
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 360, lineHeight: 1.5 }}>
              Personalize your child’s keepsake with your favorite photos, memories, and a special note — all in a few simple steps.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              {BG_IMAGES.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setBgIndex(i)}
                  sx={{
                    width: bgIndex === i ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: bgIndex === i ? '#fff' : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.4s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Right form ── */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
          mx: 'auto',
        }}
      >
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <img src='https://makemyvity.com/myVityLogoBlack.webp' alt="Company Logo" width="108" />
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {titles[mode].heading}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {titles[mode].sub}
          </Typography>
        </Box>

       {mode !== 'forgot' && (
        <>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<GoogleIcon />}
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
            color="inherit"
            sx={{ mb: 3, py: 1.25 }}
          >
            Continue with Google
          </Button>

          <Divider sx={{ mb: 3, typography: 'body2', color: 'text.secondary' }}>
            OR
          </Divider>
        </>
      )}

        {mode === 'signup' && (
          <TextField
            label="Full Name"
            placeholder="Your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            margin="normal"
          />
        )}

        <TextField
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
          margin="normal"
        />

        {mode !== 'forgot' && (
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder={mode === 'signup' ? 'At least 8 characters' : 'Enter your password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {mode === 'login' && (
          <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => switchMode('forgot')}
              underline="hover"
              color="text.secondary"
            >
              Forgot password?
            </Link>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={loading || resetLoading}
          sx={{ mt: 2, mb: 2, py: 1.5 }}
        >
          {mode === 'forgot'
            ? resetLoading
              ? 'Sending...'
              : 'Send Reset Link'
            : mode === 'login'
              ? 'Sign In'
              : 'Create Account'}
        </Button>

        {/* Password strength */}
        {mode === 'signup' && password.length > 0 && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
            {[
              { label: 'At least 8 characters', met: password.length >= 8 },
              { label: 'Contains a number', met: /\d/.test(password) },
              { label: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
            ].map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: i < 2 ? 1 : 0 }}>
                <Box
                  sx={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: 1, borderColor: r.met ? 'success.main' : 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: r.met ? 'success.light' : 'transparent',
                  }}
                >
                  {r.met && <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />}
                </Box>
                <Typography variant="caption" color={r.met ? 'success.main' : 'text.secondary'}>
                  {r.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Switch mode */}
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 4 }}>
          {mode === 'forgot' ? (
            <>Remember your password? <Link component="button" onClick={() => switchMode('login')} fontWeight="bold" underline="hover">Back to Sign In</Link></>
          ) : mode === 'login' ? (
            <>Don't have an account? <Link component="button" onClick={() => switchMode('signup')} fontWeight="bold" underline="hover">Sign Up</Link></>
          ) : (
            <>Already have an account? <Link component="button" onClick={() => switchMode('login')} fontWeight="bold" underline="hover">Sign In</Link></>
          )}
        </Typography>

        {mode === 'signup' && (
          <Typography
              variant="caption"
              align="center"
              color="text.secondary"
              sx={{ mt: 3, display: 'block' }}
            >
              By creating an account, you agree to our{' '}
              <Link
                href="https://makemyvity.com/terms.pdf"
                underline="hover"
                color="inherit"
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                href="https://makemyvity.com/privacy-policy.pdf"
                underline="hover"
                color="inherit"
              >
                Privacy Policy
              </Link>
            </Typography>
          )}
      </Box>
    </Box>
  );
}